import * as React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import { ReactComponent as ArrowLeft } from 'assets/arrow-left.svg'
import { ReactComponent as ArrowRight } from 'assets/arrow-right.svg'
import Link from 'theme/atoms/Link'
import { Helmet } from 'react-helmet'
import config from 'config'
import useTranslations from 'hooks/useTranslations'

type Props = {
  nbPages: number
  rootPathname: string
  activePage: number
  pageIndices: (number | '...')[]
  scrollRef: React.MutableRefObject<HTMLElement | null>
}

export default function LinkedPagination(props: Props) {
  const t = useTranslations<'molecules-Pagination'>()
  const { nbPages, rootPathname, activePage, pageIndices } = props

  const pageLinks = React.useMemo(() => {
    const links: { to: string; label: string; active: boolean }[] = []
    for (const index of pageIndices) {
      links.push({
        to: index === 1 ? rootPathname : `${rootPathname}page-${index}`,
        label: index.toString(),
        active: index === activePage + 1
      })
    }
    return links
  }, [nbPages, rootPathname, activePage, pageIndices])

  const prevLink = React.useMemo(() => {
    if (activePage === 0) return null
    return activePage === 1
      ? rootPathname
      : `${rootPathname}page-${activePage}/`
  }, [activePage, rootPathname])

  const nextLink = React.useMemo(() => {
    if (activePage === nbPages - 1) return null
    return `${rootPathname}page-${activePage + 2}/`
  }, [activePage, nbPages, rootPathname])

  const nextLinkWithLocale = `/${config.locale}${nextLink}`
  const prevLinkWithLocale = `/${config.locale}${prevLink}`

  const filters = typeof window === 'undefined' ? '' : window.location.hash

  const handlePageChange = () => {
    props.scrollRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  }

  if (pageLinks.length <= 1) {
    return null
  }

  return (
    <Wrapper className="LinkedPagination" data-cy-ctx="molecules/Pagination">
      <Helmet>
        {/* eslint-disable-next-line @kaminrunde/firescout/jsx-expression-needs-state */}
        {prevLink && <link rel="prev" href={prevLinkWithLocale} />}
        {/* eslint-disable-next-line @kaminrunde/firescout/jsx-expression-needs-state */}
        {nextLink && <link rel="next" href={nextLinkWithLocale} />}
      </Helmet>

      {prevLink && (
        <Li
          className={'left'}
          to={prevLink && (prevLink + filters)}
          $selected={false}
          onClick={() => prevLink && handlePageChange()}
          data-cy-state={'pagination-arrow-left-shown'}
          data-cy-handle={'pagination-arrow-left'}
          aria-label={t.asText(false, 'BFSG_previous_page')}
        >
          <ArrowLeft />
        </Li>
      )}

      {pageLinks.map((link, i) => {
        if (link.label === '...') {
          return (
            <div className="filler-dots" key={`filler-${i}`}>
              {link.label}
            </div>
          )
        }
        return (
          <Li
            key={link.to}
            to={!link.active ? link.to + filters : undefined}
            $selected={link.active}
            onClick={() => !link.active && handlePageChange()}
            data-cy-handle={'pagination-number'}
            data-cy-state={link.active ? 'selected' : 'not-selected'}
            aria-label={t.asText(false, 'BFSG_to_linked_page', {
              PAGE_NAME: link.label
            })}
          >
            {link.label}
          </Li>
        )
      })}

      {nextLink && (
        <Li
          className={'right'}
          $selected={false}
          to={nextLink && (nextLink + filters)}
          onClick={() => nextLink && handlePageChange()}
          data-cy-state={'pagination-arrow-right-shown'}
          data-cy-handle={'pagination-arrow-right'}
          aria-label={t.asText(false, 'BFSG_next_page')}
        >
          <ArrowRight />
        </Li>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.ul`
  display: flex;
  justify-content: center;
  align-items: center;
    gap: ${theme.spacing('xs')};
`
const Li = styled(Link)<{ $selected: boolean }>`
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  ${theme.ty('rc-base', 'bold')}
  color: ${theme.colors.shade.b2};
  cursor: ${(p) => p.$selected ? 'default' : 'pointer'};

  ${(p) =>
    p.$selected &&
    `
    color: ${theme.colors.b0};
    background-color: ${theme.colors.shade.b6};
    border-radius: 50%;
    cursor: default;
  `}

  &.left,
  &.right {
    > svg {
      width: 14px;
      height: 14px;
      > path {
        stroke: ${theme.colors.b0};
      }
    }
  }
`
